import { Anchor, Card, Grid, Group, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { CLICK_TRACKING_SUBDOMAIN_REGEX, DEFAULT_CLICK_TRACKING_SUBDOMAIN } from '../../constants/clickTracking';
import useFetch from '../../helpers/useFetch';
import useIsSuperAdmin from '../../helpers/useIsSuperAdmin';
import ClickTrackingHostHealth from '../ClickTrackingHostHealth';
import DnsRecordsTable, { DnsRecordRow } from '../DnsRecordsTable';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';

// The click-tracking CNAME gates campaigns of every channel, so it belongs on the
// campaign-wide messaging tab rather than with the email settings, with the
// super-admin input that decides its hostname directly above it.
const ClickTrackingSection = ({ domain, currentDomain, handleChange }) => {
  const fetch = useFetch();
  const isSuperAdmin = useIsSuperAdmin();
  const [verificationStatus, setVerificationStatus] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/verificationStatus`)
      .then((status) => {
        if (!cancelled) {
          setVerificationStatus(status);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setError(error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentDomain.accountId, currentDomain.id, fetch]);

  return (
    <Card withBorder mt="md">
      <Card.Section p="md">
        <Text size="sm" fw={500} mb="md">
          Click Tracking
        </Text>
        {isSuperAdmin && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <Group gap="xs">
                <label htmlFor="clickTrackingSubdomain">Click Tracking Subdomain</label>
                <SuperAdminBadge show />
              </Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TextInput
                id="clickTrackingSubdomain"
                name="clickTrackingSubdomain"
                placeholder={DEFAULT_CLICK_TRACKING_SUBDOMAIN}
                value={domain.clickTrackingSubdomain ?? ''}
                error={
                  domain.clickTrackingSubdomain && !CLICK_TRACKING_SUBDOMAIN_REGEX.test(domain.clickTrackingSubdomain)
                    ? 'must be a single DNS label (lowercase letters, digits, hyphens; 1-63 chars)'
                    : null
                }
                onChange={handleChange}
              />
              <Text size="xs" c="dimmed" mt="xs">
                <strong>Note to super admins:</strong> the subdomain used to build the click-tracking URL under the
                customer's sender domain (e.g. <strong>{DEFAULT_CLICK_TRACKING_SUBDOMAIN}</strong>.example.com). Leave
                blank to use the default (<strong>{DEFAULT_CLICK_TRACKING_SUBDOMAIN}</strong>). Customers must point a
                CNAME for this hostname at our tracking domain - it is required for all campaign sending, not just
                email.
              </Text>
            </Grid.Col>
          </Grid>
        )}
        <Text size="xs" c="dimmed" mb="xs">
          Every channel's links - text messages, Viber, WhatsApp, push notifications and email - are wrapped through
          this hostname, so campaigns cannot be activated until the CNAME below resolves.
        </Text>
        {error ? (
          <ErrorMessage message={error.message} />
        ) : verificationStatus === undefined ? (
          <Spinner />
        ) : verificationStatus.emailSenderDomain ? (
          <>
            <DnsRecordsTable>
              <DnsRecordRow
                record={verificationStatus.releva?.resources ?? {}}
                idPrefix="clickTracking"
                defaultType="CNAME"
              />
            </DnsRecordsTable>
            <ClickTrackingHostHealth accountId={currentDomain.accountId} domainId={currentDomain.id} />
          </>
        ) : (
          // No sender domain means no record to derive - a setup step, not the support
          // case the fallback in DnsRecordRow covers for a sender domain we cannot read.
          <Text ta="center" c="dimmed">
            Set a sender domain on the{' '}
            <Anchor
              href={`#/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/settings/messagingAndAds?tab=emails`}
            >
              Emails tab
            </Anchor>{' '}
            first - the click-tracking record is derived from it.
          </Text>
        )}
      </Card.Section>
    </Card>
  );
};

export default ClickTrackingSection;
